/**
 * 本机 agent 配置客户端 (C 类) —— 阶段 A 接入 `/api/agent/config`。
 *
 * 阶段 A 期间所有 SPA 用户访问的是 hub 主机的同一份 agent-local.json；阶段 B
 * 拆分后会变成「你电脑上 agent 进程的本地配置」，每个用户各自一份。Schema
 * 阶段 A/B 不变，只是物理位置 / ownership 变。
 *
 * 写入 PUT /api/agent/config 防抖；带副作用提示（重启 agent 才生效的字段）。
 */
import { create } from 'zustand';
import { buildUrl } from './api';
import type { HostToolName } from './api';
import { API_CREDENTIALS } from './apiBase';

/* ----------------------------------------------------------------- */
/* Schema (mirror of src/config/index.ts AgentLocalConfig)           */
/* ----------------------------------------------------------------- */

export interface HostPort {
  host: string;
  port: number;
}

export interface AgentLocalConfig {
  web: HostPort;
  devServer: HostPort;
  /** `null` = 用 `<dataDir>/devices` 默认目录；非空必须是绝对路径。 */
  devices: { dir: string | null };
  adb: { path: string };
  mirror: { startupTimeoutMs: number; socketAcceptTimeoutMs: number };
  wireless: { autoProvision: boolean };
  tcpipMirror: { enabled: boolean };
  hostTools: {
    androidSdkRoot: string | null;
    paths: Partial<Record<HostToolName, string | null>>;
  };
}

/** 字段集合：改完需要重启 agent / hub 进程才生效。SPA 用此渲染重启提示。 */
export const RESTART_REQUIRED_FIELDS: ReadonlyArray<string> = Object.freeze([
  'web.host',
  'web.port',
  'devServer.host',
  'devServer.port',
  'adb.path',
]);

/* ----------------------------------------------------------------- */
/* Defaults — used until first fetch resolves                        */
/* ----------------------------------------------------------------- */

const defaultConfig: AgentLocalConfig = {
  web: { host: '127.0.0.1', port: 10987 },
  devServer: { host: '127.0.0.1', port: 5173 },
  devices: { dir: null },
  adb: { path: 'adb' },
  mirror: { startupTimeoutMs: 20000, socketAcceptTimeoutMs: 15000 },
  wireless: { autoProvision: false },
  tcpipMirror: { enabled: true },
  hostTools: { androidSdkRoot: null, paths: {} },
};

/* ----------------------------------------------------------------- */
/* Store                                                             */
/* ----------------------------------------------------------------- */

type DeepPartial<T> = T extends object ? { [K in keyof T]?: DeepPartial<T[K]> } : T;

export interface AgentConfigState {
  config: AgentLocalConfig;
  defaults: AgentLocalConfig;
  configFile: string | null;
  ready: boolean;
  saving: boolean;
  lastError: string | null;

  init(): Promise<void>;
  refresh(): Promise<void>;
  /**
   * Optimistic update — applies `patch` to the in-memory config and schedules
   * a debounced PUT. Server response replaces the optimistic state with the
   * canonical version (so out-of-range integer trims and similar surface).
   */
  update(patch: DeepPartial<AgentLocalConfig>): void;
  /** Force-flush pending PUT. */
  flush(): Promise<void>;
}

const DEBOUNCE_MS = 300;

interface PendingState {
  patch: DeepPartial<AgentLocalConfig> | null;
  timer: ReturnType<typeof setTimeout> | null;
  inflight: Promise<void> | null;
}

const pending: PendingState = { patch: null, timer: null, inflight: null };

export const useAgentConfigStore = create<AgentConfigState>((set, get) => ({
  config: defaultConfig,
  defaults: defaultConfig,
  configFile: null,
  ready: false,
  saving: false,
  lastError: null,

  async init() {
    if (get().ready) return;
    await get().refresh();
  },

  async refresh() {
    try {
      const res = await fetch(buildUrl('/api/agent/config'), { credentials: API_CREDENTIALS });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as {
        config: AgentLocalConfig;
        defaults: AgentLocalConfig;
        configFile: string | null;
      };
      set({
        config: body.config,
        defaults: body.defaults,
        configFile: body.configFile,
        ready: true,
        lastError: null,
      });
    } catch (e) {
      set({ ready: true, lastError: (e as Error).message });
    }
  },

  update(patch) {
    set((s) => ({ config: deepMerge(s.config, patch) }));
    pending.patch = pending.patch ? deepMerge(pending.patch, patch) : patch;
    if (pending.timer) clearTimeout(pending.timer);
    pending.timer = setTimeout(() => {
      void drain(set, get);
    }, DEBOUNCE_MS);
  },

  async flush() {
    if (pending.timer) {
      clearTimeout(pending.timer);
      pending.timer = null;
    }
    if (pending.inflight) await pending.inflight;
    if (!pending.patch) return;
    await drain(set, get);
  },
}));

async function drain(
  set: (partial: Partial<AgentConfigState>) => void,
  _get: () => AgentConfigState,
): Promise<void> {
  if (pending.inflight) await pending.inflight;
  const patch = pending.patch;
  pending.patch = null;
  pending.timer = null;
  if (!patch) return;
  set({ saving: true });
  pending.inflight = (async () => {
    try {
      const res = await fetch(buildUrl('/api/agent/config'), {
        method: 'PUT',
        credentials: API_CREDENTIALS,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { detail?: string; error?: string };
        const detail = body.detail ?? body.error ?? `HTTP ${res.status}`;
        throw new Error(detail);
      }
      const body = (await res.json()) as {
        config: AgentLocalConfig;
        configFile: string | null;
        persisted: boolean;
        persistError?: string;
      };
      set({
        config: body.config,
        configFile: body.configFile,
        saving: false,
        lastError: body.persisted ? null : body.persistError ?? '保存失败',
      });
    } catch (e) {
      set({ saving: false, lastError: (e as Error).message });
    } finally {
      pending.inflight = null;
    }
  })();
  await pending.inflight;
}

/* ----------------------------------------------------------------- */
/* Hooks                                                             */
/* ----------------------------------------------------------------- */

/**
 * Read-only hook on the C-tier config. Components scope to a slice via
 * `selector` so a maxSize change doesn't re-render the SDK paths card.
 */
export function useAgentConfig<T = AgentLocalConfig>(
  selector?: (c: AgentLocalConfig) => T,
): T {
  return useAgentConfigStore((s) => (selector ? selector(s.config) : (s.config as unknown as T)));
}

export function useAgentConfigUpdate(): (patch: DeepPartial<AgentLocalConfig>) => void {
  return useAgentConfigStore((s) => s.update);
}

export function useAgentConfigStatus(): {
  ready: boolean;
  saving: boolean;
  lastError: string | null;
  configFile: string | null;
} {
  // 字段拆开订阅，避免 useSyncExternalStore 拿到字面量新对象触发死循环
  // （详细原因见 myPrefs.useMyPrefsStatus 注释）。
  const ready = useAgentConfigStore((s) => s.ready);
  const saving = useAgentConfigStore((s) => s.saving);
  const lastError = useAgentConfigStore((s) => s.lastError);
  const configFile = useAgentConfigStore((s) => s.configFile);
  return { ready, saving, lastError, configFile };
}

/* ----------------------------------------------------------------- */
/* Helpers                                                           */
/* ----------------------------------------------------------------- */

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function deepMerge<T>(base: T, patch: unknown): T {
  if (patch === undefined) return base;
  if (patch === null) return null as unknown as T;
  if (Array.isArray(patch)) return patch as unknown as T;
  if (!isPlainObject(patch)) return patch as T;
  if (!isPlainObject(base)) return patch as unknown as T;
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) continue;
    out[k] = deepMerge((base as Record<string, unknown>)[k], v);
  }
  return out as T;
}
